<template>
  <div class="content">
    <div class="container-fluid">
      <v-card :loading="saving">
        <v-card-title>Upload Receipts</v-card-title>
        <v-card-text>
          <form>
            <div class="row">
              <div class="col-md-12 photo-field">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn :disabled="!photo" v-bind="attrs" v-on="on" @click.prevent="togglePreview()">
                      <v-icon v-if="!showPreview">mdi-text-box-search</v-icon>
                      <v-icon v-if="showPreview">mdi-text-box-remove</v-icon>
                    </v-btn>

                  </template>
                  <div>Toggle Preview</div>
                </v-tooltip>
                <base-input id="photoInput" v-model="receipt.photo" :disabled="loading || saving" label="Receipt Photo*"
                            placeholder="photo" type="file"></base-input>
              </div>
            </div>
            <div class="row pt-3">
              <div class="col-md-4">

                <base-input label="Receipt Date*"
                            placeholder="Date">
                  <div>
                    <date-picker v-model="receipt.date" :disabled="loading || saving" format="DD/MM/YYYY"></date-picker>
                  </div>
                </base-input>

              </div>
              <div class="col-md-4">
                <base-input v-model="receipt.amount" :disabled="loading || saving" label="Receipt Amount*"
                            placeholder="Amount" type="number"></base-input>
              </div>
              <div class="col-md-4">
                <div class="form-group">
                  <label class="control-label">
                    Kind*
                  </label>
                  <div>
                    <select v-model="receipt.kind" :disabled="loading || saving">
                      <option value="">Other</option>
                      <option value="sport">Sport Activity</option>
                      <option value="park">Parking</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </v-card-text>
        <v-card-actions>
          <v-btn :disabled="loading || saving" class="primary" type="submit" @click.prevent="uploadReceipts()">
            <i v-if="saving && receipts" class="fa fa-hourglass-2"></i>
            <i v-if="!saving || !receipts" class="fa fa-cloud-upload"></i>
            Upload
          </v-btn>
          <v-btn type="reset" @click="reset()">
            Reset
          </v-btn>

        </v-card-actions>
        <div v-if="showPreview && photo" class="photo-preview">
          <div><b>Receipt Preview:</b></div>
          <img :src="photo">
        </div>
      </v-card>

      <div class="pt-4">
        <v-card :loading="loading">
          <v-card-title>Uploaded Receipts</v-card-title>
          <v-card-text>
            <v-data-table v-if="receipts" :headers="headers" :items="receipts"
                          :items-per-page="15" :sort-desc="true" class="elevation-1" sort-by="date">

              <template v-slot:item.id="{ item }">
                <div class="receipt-actions">
                  <v-tooltip v-if="!item.deleted" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon :disabled="!item.photoPath" v-bind="attrs" @click="openReceipt(item)" v-on="on">
                        mdi-download-box
                      </v-icon>
                    </template>
                    <div>Download</div>
                  </v-tooltip>

                  <v-tooltip v-if="!item.deleted && !item.approved" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" @click="deleteReceipt(item)" v-on="on">mdi-delete</v-icon>
                    </template>
                    <div>Delete</div>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:item.amount="{ item }">
                ₪ {{ item.amount }}
              </template>

              <template v-slot:item.approved="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-if="item.approved" color="green" v-bind="attrs" v-on="on">mdi-text-box-check</v-icon>
                    <v-icon v-if="!item.approved" color="orange" v-bind="attrs" v-on="on">mdi-text-box-plus</v-icon>
                  </template>
                  <div v-if="item.approved">Payment Approved</div>
                  <div v-if="!item.approved">New (Pending Approval)</div>
                </v-tooltip>
              </template>
              <template v-slot:item.created="{ item }">
                <div class="text-cell">{{ formatDate(item.created) }}</div>
              </template>
              <template v-slot:item.date="{ item }">
                <div class="text-cell">{{ formatDate(item.date) }}</div>
              </template>

              <template v-slot:item.kind="{ item }">
                <div>
                  <v-tooltip v-if="item.kind==='sport'" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">mdi-weight-lifter</v-icon>
                    </template>
                    <div>Sport Activities</div>
                  </v-tooltip>
                  <v-tooltip v-if="item.kind==='park'" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">mdi-parking</v-icon>
                    </template>
                    <div>Parking</div>
                  </v-tooltip>
                  <v-tooltip v-if="!item.kind" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on">mdi-cash-multiple</v-icon>
                    </template>
                    <div>Other</div>
                  </v-tooltip>
                  <span class="kind-name">{{ getKindName(item.kind) }}</span>
                </div>
              </template>

            </v-data-table>
            <div v-if="!receipts && !loading">
              No receipts found.
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue2-datepicker/index.css';
import '../components/../components/Inputs/date.css';
import DatePicker from 'vue2-datepicker';
import { notificationService } from '../services/NotificationsService';
import { collection, doc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore';
import { firebase } from '../services/FirebaseService';
import moment from 'moment';
import { randomString } from '../services/crypto.util';
import { first } from 'rxjs';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { fileExtension } from '../services/file.util';

function newReceipt() {
  const currentUser = firebase.auth.currentUser;
  return {
    id: currentUser ? `${currentUser.uid}-${randomString(32)}-${Date.now().toString(16)}` : null,
    photoRand: randomString(32),
    date: '',
    amount: '',
    kind: '',
    photo: null,
  };
}

export default {
  components: { DatePicker },
  data: () => ({
    loading: false,
    saving: false,
    receipt: newReceipt(),
    photo: null,
    receipts: null,
    showPreview: false,
    headers: [
      { text: 'Status', value: 'approved' },
      { text: 'Date', value: 'date' },
      { text: 'Uploaded', value: 'created' },
      { text: 'Amount', value: 'amount' },
      { text: 'Kind', value: 'kind' },
      { text: 'Actions', value: 'id' },
    ],
  }),

  mounted() {
    this.db = getFirestore();
    this.storage = getStorage();

    // wait for user to load
    firebase.user$.pipe(first()).subscribe(() => {
      // create empty receipt
      this.receipt = newReceipt();

      // load receipts
      this.task('loading', async () => this.loadReceipts());
    });
  },

  watch: {
    'receipt.photo': function() {
      const file = this.photoFile();
      if (file && file.type.startsWith('image/')) {
        this.photo = URL.createObjectURL(file);
      } else {
        this.photo = null;
      }
    },
  },

  methods: {
    formatDate(date) {
      return date && moment(date).format('DD/MM/YYYY');
    },
    getKindName(kind) {
      switch (kind) {
        case 'sport':
          return 'Sport';
        case 'park':
          return 'Parking';
      }
      return 'Other';
    },

    async openReceipt(receipt) {
      const downloadUrl = await getDownloadURL(ref(this.storage, receipt.photoPath));
      window.open(downloadUrl, '_blank');
    },

    async deleteReceipt(receipt) {
      if (!confirm('Are you sure you want to delete this receipt?')) {
        return;
      }
      await this.task('loading', async () => {
        const receiptRef = doc(this.db, 'receipts', receipt.id);
        await setDoc(receiptRef, { deleted: new Date() }, { merge: true });
        await this.loadReceipts();
      });
    },

    async loadReceipts() {
      const currentUser = firebase.auth.currentUser;
      const results = await getDocs(query(collection(this.db, 'receipts'),
        where('user', '==', currentUser.email),
        where('deleted', '==', null)));

      const receipts = [];
      results.forEach((doc) => {
        const data = doc.data();
        receipts.push({
          id: doc.id,
          date: moment(data.date.seconds * 1000).format('YYYY-MM-DD'),
          amount: data.amount.toFixed(2),
          kind: data.kind,
          photoPath: data.photoPath,
          approved: data.approved,
          created: data.created && moment(data.created.seconds * 1000).format('YYYY-MM-DD'),
          Status: data.approved ? 'Approved' : 'New',
          deleted: data.deleted,
        });
      });

      this.receipts = receipts;
    },

    photoFile() {
      const photoInput = document.getElementById('photoInput');
      const file = photoInput.files && photoInput.files.length && photoInput.files[0];
      return file;
    },

    async task(flagName, runnable) {
      this[flagName] = true;
      try {
        await runnable();
      } finally {
        this[flagName] = false;
      }
    },

    async uploadReceipts() {
      const error = this.validate();
      if (error) {
        notificationService.error(error + '. Fix the form and try again.');
        return;
      }
      await this.task('saving', async () => {
        const receipt = this.receipt;
        const currentUser = firebase.auth.currentUser;

        // upload receipt
        const storage = this.storage;
        const photoFile = this.photoFile();
        let photoPath;
        if (receipt.kind === 'sport' && !photoFile) {
          photoPath = null;
        } else {
          const extension = fileExtension(photoFile.name);
          photoPath = `receipts/${currentUser.uid}/${receipt.id}-${receipt.photoRand}.${extension}`;
          const storageRef = ref(storage, photoPath);
          const snapshot = await uploadBytes(storageRef, photoFile);
        }

        // store receipt data
        const data = {
          user: currentUser.email,
          kind: receipt.kind,
          amount: parseInt(receipt.amount),
          created: new Date(),
          date: moment(receipt.date).toDate(),
          approved: null,
          deleted: null,
          photoPath,
        };
        await setDoc(doc(this.db, 'receipts', receipt.id), data);
        notificationService.success('Receipt uploaded');
        this.receipt = newReceipt();

        await this.task('loading', async () => this.loadReceipts());
      });
    },

    validate() {
      const receipt = this.receipt;
      if (!receipt.date) {
        return 'Invalid Receipt Date';
      }
      if (isNaN(parseInt(receipt.amount))) {
        return 'Invalid Receipt Amount';
      }
      if (!receipt.photo && receipt.kind !== 'sport') {
        return 'Receipt Photo is required';
      }
    },

    reset() {
      this.receipt = newReceipt();
    },

    togglePreview() {
      this.showPreview = !this.showPreview;
    },
  },
};
</script>
<style lang="scss">
h4 {
  margin: 0 0 1rem;
}

.photo-field {
  button {
    min-width: 0 !important;
    padding: 6px !important;
    float: right;
    margin-top: 23px;
  }

  .form-group {
    margin-right: 40px;
    position: relative;
  }
}

.photo-preview {
  padding: 1rem;

  img {
    max-width: 100%;
  }
}

.receipt-actions button {
  margin-left: 0.5rem;
}

.form-group select {
  background-color: #FFFFFF;
  border: 1px solid #E3E3E3;
  border-radius: 4px;
  color: #565656;
  padding: 8px 12px;
  height: 40px;
  width: 100%;
}

.kind-name {
  padding-left: 1rem;
}

select {
  background: url("/img/icons/chevron-down.svg") 98% center no-repeat;
}
</style>
